import { PlusCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import AdvancedTable, {
  AdvancedTableRef,
  ColumnTitle,
  EditableColumnType,
  getActionColumns,
} from "../components/AdvancedTable";
import { DataConfirm } from "../components/DataConfirm";
import { ContextType, DataProvider, DataType } from "../contexts/DataProvider";
import { Filter } from "../shared/protocols/editor/PtlDataFind";
import { dataCall } from "../tsrpc";
const { Title } = Typography;

interface ErrorCodeData extends DataType {
  _id: string;
  code: string;
  module: string;
  explain: string;
  comment?: string;
  apis: string[];
}

interface ApiData extends DataType {
  _id: string;
  name: string;
}

const ErrorCodeContext = createContext<ContextType<ErrorCodeData> | null>(null);
const ApiContext = createContext<ContextType<ApiData> | null>(null);

export const PageErrorCode = () => (
  <DataProvider<ApiData>
    context={ApiContext}
    collection="apis"
    limit={1024}
    sort={{ name: 1 }}
  >
    <DataProvider<ErrorCodeData>
      context={ErrorCodeContext}
      collection="errorcodes"
      sort={{ code: 1 }}
    >
      <InnerComponent />
    </DataProvider>
  </DataProvider>
);

function InnerComponent() {
  const [editingData, setEditingData] = useState<ErrorCodeData>();
  const [filterErrorCode, setFilterErrorCode] = useState<Filter>();
  const [filterName, setFilterName] = useState<Filter>();
  const [filterExplain, setFilterExplain] = useState<Filter>();
  const [filterApis, setFilterApis] = useState<Filter>();
  const [modules, setModules] = useState<string[]>([]);
  const [searchExplain, setSearchExplain] = useState<string>();
  const errorcodes = useContext<ContextType<ErrorCodeData> | null>(
    ErrorCodeContext
  );
  const apis = useContext<ContextType<ApiData> | null>(ApiContext);
  const tableRef = useRef<AdvancedTableRef<ErrorCodeData>>(null);

  useEffect(() => {
    (async () => {
      await apis?.load();
      const res = await dataCall("editor/DataDistinct", {
        collection: "errorcodes",
        field: "module",
      });
      if (res.isSucc) setModules(res.res.data);
    })();
  }, []);

  const columns: EditableColumnType<ErrorCodeData>[] = [
    {
      title: (
        <ColumnTitle title="错误码">
          <Input.Search
            style={{ flex: 2 }}
            allowClear
            maxLength={64}
            onSearch={(v) => {
              if (v) setFilterErrorCode({ code: { $regex: v } });
              else setFilterErrorCode(undefined);
            }}
          />
        </ColumnTitle>
      ),
      width: 240,
      dataIndex: "code",
      render: (v: string) => (
        <Space align="baseline" style={{ fontSize: "1.2em" }}>
          <span>{v?.slice?.(0, 1)}</span>
          <span>{v?.slice?.(1, 3)}</span>
          <span>{v?.slice?.(3)}</span>
        </Space>
      ),
      editor: <Input allowClear maxLength={32} />,
    },
    {
      title: (
        <ColumnTitle title="名称">
          <Input.Search
            style={{ flex: 2 }}
            allowClear
            maxLength={64}
            onSearch={(v) => {
              if (v) setFilterName({ name: { $regex: v, $options: "i" } });
              else setFilterName(undefined);
            }}
          />
        </ColumnTitle>
      ),
      width: 240,
      dataIndex: "name",
      editor: <Input allowClear maxLength={32} />,
    },
    {
      title: "模块",
      width: 120,
      dataIndex: "module",
      editor: <Input />,
      filters: modules.map((m) => ({ text: m, value: m })),
    },
    {
      title: (
        <ColumnTitle title="含义">
          <Input.Search
            style={{ flex: 2 }}
            allowClear
            value={searchExplain}
            onChange={(e) => setSearchExplain(e.target.value)}
            onSearch={(v) => {
              if (v) setFilterExplain({ explain: { $regex: v } });
              else setFilterExplain(undefined);
            }}
          />
        </ColumnTitle>
      ),
      width: 240,
      dataIndex: "explain",
      editor: <Input />,
    },
    {
      title: "备注",
      dataIndex: "comment",
      render: (v) => <p style={{ whiteSpace: "pre-line" }}>{v}</p>,
      editor: <Input.TextArea />,
    },
    {
      title: (
        <ColumnTitle title="涉及接口">
          <Select
            allowClear
            style={{ flex: 2 }}
            options={apis?.data.map((api) => ({
              label: api.name,
              value: api.name,
            }))}
            onChange={(v) => setFilterApis(v ? { apis: v } : undefined)}
          />
        </ColumnTitle>
      ),
      dataIndex: "apis",
      render: (value) => (
        <Flex gap={0} wrap="wrap">
          {value?.map((v: string, i: number) => (
            <Tag key={i}>{v}</Tag>
          ))}
        </Flex>
      ),
      editor: (
        <Select
          mode="multiple"
          options={apis?.data.map((api) => ({
            label: api.name,
            value: api.name,
          }))}
        />
      ),
    },
    getActionColumns<ErrorCodeData>({
      editingData,
      setEditingData,
      display: (v) => v?.code,
      editSubmit: () => tableRef.current?.editSubmit,
      $delete: () => tableRef.current?.delete,
    }),
  ];

  return apis && errorcodes ? (
    <AdvancedTable
      ref={tableRef}
      context={errorcodes}
      columns={columns}
      editingData={editingData}
      filter={{
        ...filterErrorCode,
        ...filterName,
        ...filterExplain,
        ...filterApis,
      }}
      title={() => (
        <Flex justify="space-between" align="center">
          <Title level={5}>错误码列表</Title>
          <Flex gap={6}>
            <DataConfirm<ErrorCodeData>
              title="新增错误码"
              component={
                <Button icon={<PlusCircleOutlined />}>添加错误码</Button>
              }
              onSubmit={(value) => errorcodes.add(value)}
            >
              <Form.Item name="code" label="错误码">
                <Input allowClear maxLength={32} />
              </Form.Item>
              <Form.Item name="name" label="名称">
                <Input allowClear maxLength={32} />
              </Form.Item>
              <Form.Item name="module" label="模块">
                <Input />
              </Form.Item>
              <Form.Item name="explain" label="含义">
                <Input />
              </Form.Item>
              <Form.Item name="comment" label="备注">
                <Input.TextArea />
              </Form.Item>
              <Form.Item label="涉及接口">
                <Form.Item name="apis">
                  <Select
                    mode="multiple"
                    options={apis?.data.map((api) => ({
                      label: api.name,
                      value: api.name,
                    }))}
                  />
                </Form.Item>
              </Form.Item>
            </DataConfirm>
            <DataConfirm<ApiData>
              title="新增接口"
              component={
                <Button icon={<PlusCircleOutlined />}>添加接口</Button>
              }
              onSubmit={(value) => apis.add(value)}
            >
              <Form.Item name="name" label="接口名称">
                <Input />
              </Form.Item>
            </DataConfirm>
          </Flex>
        </Flex>
      )}
    />
  ) : (
    <></>
  );
}
